<script setup>
import { ref, reactive } from 'vue';
import { API } from '@/services/index';
import { useMutation } from '@tanstack/vue-query';
import { useFileUpload } from '@/composables/useFileUpload.js';
import { usePermissionsStore } from '@/stores/permissions.store.js';
import { useUserStore } from '@/stores/user.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { useSettingsStore } from '@/stores/settings.store.js';
import { UPDATE_WORKSPACE } from '@/services/workspace/mutations/workspace';
import HeaderPartial from '@/pages/partials/settings/headerPartial.vue';
import SectionPartial from '@/pages/partials/settings/sectionPartial.vue';
import WorkspaceCollaboratorsTable from '@/pages/settings/workspace/components/workspaceCollaboratorsTable.vue';
import imagePlaceholder from '@/assets/workspace-placeholder.png';
import WorkspaceLogo from '@/components/WorkspaceLogo.vue';

const permissions = usePermissionsStore();
const workspaceImageInput = ref();
const workspaceImageFile = ref(null);
const activeAccount = useSettingsStore().activeAccount;
const activeWorkspace = computed(() => useSettingsStore().activeWorkspace);
const newWorkspaceName = ref(null);
const isArchivedWorkspace = ref(false);
const isEditWorkspaceName = ref(false);
const isWorkspaceNameFormValid = ref(false);
const updateForm = reactive({
  fieldType: null,
  isLoading: false
});

const formRules = {
  workspaceName: (value) => !!value || 'Workspace name is required'
};

watchEffect(() => {
  if (activeWorkspace.value) {
    newWorkspaceName.value = activeWorkspace.value.name;
    isArchivedWorkspace.value = activeWorkspace.value.archived;
  }
});

const workspaceMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/workspace', UPDATE_WORKSPACE, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.updateWorkspace) {
      useSettingsStore().setActiveWorkspace(data.data.data.updateWorkspace);
      useWorkspaceStore().getWorkspaces({
        account_id: Number(activeAccount.id),
        archived: false,
        admin: true
      });

      updateForm.isLoading = false;
      switch (updateForm.fieldType) {
        case 'name':
          isEditWorkspaceName.value = false;
          break;
      }
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
    updateForm.fieldType = null;
    updateForm.isLoading = false;
  }
});

const updateWorkspaceImageHandler = async (payload) => {
  updateForm.fieldType = 'logo';
  updateForm.isLoading = true;

  if (payload?.removed) {
    await workspaceMutation.mutateAsync({
      workspace_id: Number(activeWorkspace.value.id),
      remove_image: true
    });
  } else {
    const { uploadFile, uploadErrors, uploadSuccess, uploadedFileLocation } = useFileUpload();

    if (workspaceImageFile.value) {
      await uploadFile(workspaceImageFile.value);
      if (uploadSuccess.value && uploadedFileLocation.value) {
        await workspaceMutation.mutateAsync({
          workspace_id: Number(activeWorkspace.value.id),
          image: uploadedFileLocation.value
        });
      }
    }
  }
};

const updateWorkspaceNameHandler = async () => {
  updateForm.fieldType = 'name';
  updateForm.isLoading = true;

  const payload = {
    workspace_id: Number(activeWorkspace.value.id),
    name: newWorkspaceName.value
  };

  await workspaceMutation.mutateAsync(payload);
};

const updateWorkspaceArchivedHandler = async () => {
  updateForm.fieldType = 'archived';
  updateForm.isLoading = true;

  const payload = {
    workspace_id: Number(activeWorkspace.value.id),
    archived: isArchivedWorkspace.value
  };

  await workspaceMutation.mutateAsync(payload);
};
</script>

<template>
  <div class="tw-max-w-full lg:tw-max-w-[750px]">
    <HeaderPartial
      class="tw-capitalize"
      :style="!activeWorkspace ? 'color:#d8d8d8' : ''"
      :title="`${activeWorkspace ? activeWorkspace?.name + ' workspace details' : 'Workspace Details'}`"
    />
    <div class="tw-mb-2 tw-flex" v-if="activeWorkspace?.archived">
      <v-chip color="cyan-darken-4" label density="compact">
        <v-icon icon="mdi-archive" start></v-icon>
        Archived
      </v-chip>
    </div>
    <SectionPartial
      :style="!activeWorkspace ? 'color:#d8d8d8' : ''"
      label="Workspace Logo"
      :isLoading="updateForm.fieldType === 'logo' && updateForm.isLoading"
      :subtitle="permissions.canUpdateWorkspace() ? 'This helps your teammates recognize your brand in PartnrUp.' : ''"
    >
      <div
        v-if="permissions.canUpdateWorkspace()"
        class="tw-mb-6 tw-text-[0.8rem] tw-font-medium tw-text-[#001ea4]"
        :style="!activeWorkspace ? 'color:#d8d8d8' : ''"
      >
        Suggestion - Please upload a circle image with the following dimensions: width and height 300px or higher, with a transparent background.
      </div>
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-pb-5 lg:tw-flex-row">
        <div class="tw-flex tw-items-center">
          <WorkspaceLogo v-if="activeWorkspace" :workspace="activeWorkspace" size="7rem" class-names="tw-mb-4 lg:tw-mb-0" />
        </div>
        <div
          class="tw-mt-8 tw-flex tw-w-full tw-space-x-2 lg:tw-mt-0 lg:tw-w-[16rem] lg:tw-items-center"
          v-if="permissions.canUpdateWorkspace()"
          :class="activeWorkspace?.image ? 'tw-justify-between' : 'tw-justify-end'"
        >
          <v-btn
            v-if="activeWorkspace?.image"
            class="tw-capitalize"
            variant="tonal"
            size="small"
            height="40px"
            :disabled="!activeWorkspace || (updateForm.fieldType === 'logo' && updateForm.isLoading)"
            @click="updateWorkspaceImageHandler({ removed: true })"
          >
            Remove Logo
          </v-btn>
          <v-btn
            class="tw-capitalize"
            variant="tonal"
            size="small"
            height="40px"
            color="primary-blue"
            @click="workspaceImageInput.click()"
            :disabled="!activeWorkspace"
            :isLoading="updateForm.fieldType === 'logo' && updateForm.isLoading"
          >
            {{ activeWorkspace?.image ? 'Change Logo' : 'Upload Logo' }}
          </v-btn>
        </div>
        <v-file-input
          v-model="workspaceImageFile"
          ref="workspaceImageInput"
          accept="image/*"
          class="tw-hidden"
          @update:modelValue="updateWorkspaceImageHandler"
        ></v-file-input>
      </div>
    </SectionPartial>
    <SectionPartial
      :style="!activeWorkspace ? 'color:#d8d8d8' : ''"
      :label="'Workspace Name'"
      :isLoading="updateForm.fieldType === 'name' && updateForm.isLoading"
    >
      <template v-if="!isEditWorkspaceName">
        <div class="tw-flex tw-items-center tw-justify-between">
          <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">
            {{ activeWorkspace?.name }}
          </p>
          <v-btn
            v-if="permissions.canUpdateWorkspace()"
            @click="isEditWorkspaceName = true"
            class="tw-capitalize"
            variant="tonal"
            size="small"
            height="40px"
            :disabled="!activeWorkspace"
          >
            Edit
          </v-btn>
        </div>
      </template>
      <template v-else>
        <v-form ref="nameForm" v-model="isWorkspaceNameFormValid" @submit.prevent="updateWorkspaceNameHandler">
          <div class="tw-flex tw-flex-col tw-items-start tw-justify-between lg:tw-flex-row lg:tw-space-x-2">
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="newWorkspaceName"
                variant="outlined"
                color="primary-blue"
                label=""
                :rules="[formRules.workspaceName]"
                placeholder="Workspace Name*"
                hide-details="auto"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              ></v-text-field>
            </div>
            <div class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between lg:tw-ml-16 lg:tw-mt-0 lg:tw-w-[9rem] lg:tw-space-x-3">
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                @click="isEditWorkspaceName = false"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              >
                Cancel
              </v-btn>
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                type="submit"
                color="primary-blue"
                :loading="updateForm.isLoading && updateForm.fieldType === 'name'"
                :disabled="(updateForm.isLoading && updateForm.fieldType === 'name') || !isWorkspaceNameFormValid"
              >
                Save
              </v-btn>
            </div>
          </div>
        </v-form>
      </template>
    </SectionPartial>

    <!--    <SectionPartial-->
    <!--      :label="'Workspace Archived'"-->
    <!--      subtitle="Change workspace to archive mode."-->
    <!--      :isLoading="updateForm.fieldType === 'archived' && updateForm.isLoading"-->
    <!--    >-->
    <!--      <div class="tw-flex tw-items-center tw-space-x-2">-->
    <!--        <div class="tw-flex tw-items-center tw-space-x-2">-->
    <!--          <v-switch-->
    <!--            v-model="isArchivedWorkspace"-->
    <!--            @update:modelValue="updateWorkspaceArchivedHandler"-->
    <!--            color="secondary-blue"-->
    <!--            hide-details="auto"-->
    <!--            density="compact"-->
    <!--            :disabled="(updateForm.isLoading && updateForm.fieldType === 'archived') || !activeWorkspace"-->
    <!--            :loading="updateForm.isLoading && updateForm.fieldType === 'archived'"-->
    <!--            inset-->
    <!--          ></v-switch>-->
    <!--          <div>-->
    <!--            <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">-->
    <!--              {{ `Archived: ${isArchivedWorkspace.toString()}` }}-->
    <!--            </p>-->
    <!--          </div>-->
    <!--        </div>-->
    <!--      </div>-->
    <!--    </SectionPartial>-->
  </div>
  <div v-if="activeWorkspace">
    <HeaderPartial :style="!activeWorkspace ? 'color:#d8d8d8' : ''" title="Collaborators"></HeaderPartial>
    <SectionPartial :divider="false">
      <WorkspaceCollaboratorsTable />
    </SectionPartial>
  </div>
</template>

<style lang="css" scoped></style>
